import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CompanionTerms = () => {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Managerial Companion Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last updated: 18th January 2026</p>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 mb-8">
            These Terms of Service ("Terms") govern your access to and use of the managerial companion (the "Companion"), provided by 30 Day Skills. By accessing or using the Companion, you agree to these Terms. If you do not agree, do not use the Companion.
          </p>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. About the Companion</h2>
            <p className="text-gray-700 mb-4">
              The Companion is a digital service designed to support reflection and thinking about managerial and organisational situations.
            </p>
            <p className="text-gray-700">
              It is intended to assist users in clarifying their own thinking. It does not replace human judgment, experience, or professional advice.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Not Professional Advice</h2>
            <p className="text-gray-700 mb-4">
              The Companion does not provide professional, legal, financial, medical, or other regulated advice.
            </p>
            <p className="text-gray-700 mb-4">Outputs generated by the Companion:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Are general in nature</li>
              <li>May be incomplete or imperfect</li>
              <li>Should not be relied upon as definitive guidance</li>
            </ul>
            <p className="text-gray-700">
              You are solely responsible for how you interpret and act on any output. Use of the Companion does not create any advisory, coaching, employment, or fiduciary relationship.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Responsibilities</h2>
            <p className="text-gray-700 mb-4">You agree that:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>You will use the Companion only for lawful purposes</li>
              <li>You are responsible for the decisions you make and actions you take</li>
              <li>You will not rely on the Companion as the sole basis for important decisions</li>
              <li>You will not input content you do not have the right to share</li>
            </ul>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You must not use the Companion to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Violate any law or regulation</li>
              <li>Infringe intellectual property or privacy rights</li>
              <li>Input unlawful, abusive, or harmful content</li>
              <li>Attempt to reverse engineer, disrupt, or misuse the service</li>
              <li>Use the service in a way that could reasonably cause harm to others</li>
            </ul>
            <p className="text-gray-700">
              We reserve the right to suspend or terminate access if we believe these Terms have been violated.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Availability and Changes</h2>
            <p className="text-gray-700 mb-4">
              The Companion is provided on an "as-is" and "as-available" basis.
            </p>
            <p className="text-gray-700 mb-4">We may:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Modify or improve the Companion</li>
              <li>Add or remove features</li>
              <li>Suspend or discontinue the service</li>
            </ul>
            <p className="text-gray-700">
              We do not guarantee uninterrupted availability or specific outcomes.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Usage Limits</h2>
            <p className="text-gray-700 mb-4">
              To encourage thoughtful reflection and prevent overreliance on the Companion, usage is limited to <strong>20 reflections per week</strong> (Monday to Sunday), with some flexibility up to 25 in exceptional circumstances.
            </p>
            <p className="text-gray-700 mb-4">
              This limit is designed to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Encourage quality over quantity in your reflections</li>
              <li>Allow time to implement insights between sessions</li>
              <li>Ensure the Companion remains a thinking aid, not a crutch</li>
            </ul>
            <p className="text-gray-700">
              Your weekly count resets each Monday. You will receive a notification when approaching your limit.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Companion, including its design, functionality, and underlying systems, is owned by or licensed to 30 Day Skills.
            </p>
            <p className="text-gray-700 mb-4">
              You retain ownership of the content you submit, subject to the rights described in the <Link to="/companion-privacy" className="text-red-600 hover:underline">Companion Privacy & Data Use Notice</Link>.
            </p>
            <p className="text-gray-700">
              You may not copy, reproduce, or resell the Companion or its outputs except as permitted by law.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Privacy and Data Use</h2>
            <p className="text-gray-700 mb-4">
              Use of the Companion is also governed by the <Link to="/companion-privacy" className="text-red-600 hover:underline">Companion Privacy & Data Use Notice</Link>, which explains how data is collected, processed, stored, and protected.
            </p>
            <p className="text-gray-700">
              By using the Companion, you acknowledge and agree to that notice.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Suspension and Termination</h2>
            <p className="text-gray-700 mb-4">We may suspend or terminate your access to the Companion:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>If you violate these Terms</li>
              <li>If required by law</li>
              <li>If the service is discontinued</li>
            </ul>
            <p className="text-gray-700">
              You may stop using the Companion at any time.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the fullest extent permitted by law, 30 Day Skills shall not be liable for any indirect, incidental, consequential, or special damages arising from your use of, or inability to use, the Companion.
            </p>
            <p className="text-gray-700">
              Your use of the Companion is at your own risk.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify and hold harmless 30 Day Skills from any claims, losses, or expenses arising out of your use of the Companion or violation of these Terms.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700">
              These Terms are governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about these Terms, please contact:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> legal@30dayskills.co.uk
            </p>
          </section>

          <hr className="my-8 border-gray-300" />

          <section className="mb-8">
            <div className="bg-slate-100 border-l-4 border-red-600 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Acceptance</h3>
              <p className="text-gray-700 m-0">
                By accessing or using the Companion, you confirm that you have read, understood, and agree to these Terms of Service.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CompanionTerms;
